import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, User } from 'lucide-react';
import { SLOT_LABEL, STATUS_LABEL, formatDay, formatDateTime, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import { useAutoRefresh } from '../../useAutoRefresh.js';
import { addDays } from '../bookings/shared.jsx';
import { problem } from '../bookings/util.js';

// Operations > Whereabouts: where everyone is on a day. Each team's people,
// anyone off (BrightHR: holiday, sickness, TOIL, training), what the team is
// doing from its job cards, and how far through the day's jobs it is
// (the jobs' steps done, from On route to close off, as a percentage, and the
// step they're on now): the line along the top of each team's card, red at
// the start, turning amber then green as the steps are done, with a pulsing
// dot at its tip while they're at it. Refreshes itself every 30 seconds.
const KIND = { holiday: 'Holiday', sickness: 'Sick', toil: 'TOIL', other: 'Away' };
const PART = { all: '', am: ' (AM)', pm: ' (PM)' };
const ACTIVITY = {
  no_jobs: 'No jobs booked',
  not_started: 'Not started yet',
  on_route: 'On route',
  on_site: 'On site',
  between: 'Between jobs',
  finished: 'Finished for the day',
};
const time = (v) => (v ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' }).format(new Date(v)) : '');
const dayName = (d) => new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));

function Absence({ a }) {
  return (
    <span className={`tag where-off where-${a.kind}`} title={a.label || undefined}>
      {a.label || KIND[a.kind] || 'Off'}{PART[a.part] || ''}
    </span>
  );
}

// The line along the top of a team's card: as long as the share done,
// its colour going from red (0%) through amber to green (100%).
function TopLine({ p, working }) {
  const hue = Math.round((p.percent / 100) * 130);
  return (
    <div className={`where-line${working ? ' working' : ''}${p.percent >= 100 ? ' done' : ''}`} style={{ '--pct': `${p.percent}%`, '--tone': `hsl(${hue} 70% 42%)` }}
      role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={p.percent} aria-label="Day done">
      <span className="where-line-fill" />
      <span className="where-line-dot" />
    </div>
  );
}

export default function Whereabouts() {
  const [params, setParams] = useSearchParams();
  const day = params.get('day') || todayUk();
  const setDay = (d) => setParams({ day: d }, { replace: true });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => api(`/whereabouts?day=${day}`).then((d) => { setData(d); setError(null); }, (e) => setError(problem(e))), [day]);
  useEffect(() => { setData(null); load(); }, [load]);
  useAutoRefresh(load);

  const s = data?.summary;
  const jobOf = (td, id) => td.jobs.find((j) => j.id === id);

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Whereabouts</h1>
            <p className="muted">Where everyone is: each team from its job cards, and anyone off from BrightHR (holiday, sickness, TOIL, training). The line along the top of each team is how much of its day is done: red at the start, green when every job is completed.</p>
          </div>
        </div>
        <div className="filters">
          <div className="week-nav" role="group" aria-label="Day">
            <button type="button" className="btn icon-only" aria-label="Previous day" onClick={() => setDay(addDays(day, -1))}><ChevronLeft size={18} aria-hidden="true" /></button>
            <button type="button" className="btn" onClick={() => setDay(todayUk())}>Today</button>
            <button type="button" className="btn icon-only" aria-label="Next day" onClick={() => setDay(addDays(day, 1))}><ChevronRight size={18} aria-hidden="true" /></button>
            <input type="date" className="filter-date" value={day} aria-label="Day" onChange={(e) => /^\d{4}-\d{2}-\d{2}$/.test(e.target.value) && setDay(e.target.value)} />
            <span className="week-label">{dayName(day)} {formatDay(day)}</span>
          </div>
        </div>
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!data && !error && <p className="muted">Loading…</p>}
        {s && (
          <div className="stat-row where-stats">
            <div className="stat"><span className="stat-num">{s.percent}%</span><span className="stat-label">of the day done · {s.completed} of {s.jobs} jobs completed</span></div>
            <div className="stat"><span className="stat-num">{s.onSite}</span><span className="stat-label">Teams on site</span></div>
            <div className="stat"><span className="stat-num">{s.onRoute}</span><span className="stat-label">Teams on route</span></div>
            <div className={`stat${s.sick ? ' stat-warn' : ''}`}><span className="stat-num">{s.off}</span><span className="stat-label">Off{s.sick ? ` (${s.sick} sick)` : ''}</span></div>
          </div>
        )}
        {data?.leaveCheckedAt && <p className="small muted">Absences from BrightHR, last checked {formatDateTime(data.leaveCheckedAt)}.</p>}
      </section>

      {data?.teams.map((td) => {
        const a = td.activity;
        const at = a.job ? jobOf(td, a.job) : null;
        return (
          <section key={td.team.id} className={`card where-team where-${a.state}`}>
            <TopLine p={td.progress} working={['on_site', 'on_route', 'between'].includes(a.state)} />
            <div className="where-head">
              <h2>{td.team.name}</h2>
              <span className={`where-state s-${a.state}`}>
                {ACTIVITY[a.state]}
                {at && <span className="small"> · {at.address || at.postcode}</span>}
                {a.since && <span className="small"> · {a.state === 'finished' ? 'at' : 'since'} {time(a.since)}</span>}
              </span>
            </div>
            <ul className="crew crew-inline where-crew">
              {td.members.map((m) => (
                <li key={m.id} className={m.absence?.part === 'all' ? 'where-away' : ''}>
                  <User size={14} aria-hidden="true" />{m.name}
                  {m.absence && <Absence a={m.absence} />}
                </li>
              ))}
              {!td.members.length && <li className="muted">Nobody in this team on this day</li>}
            </ul>
            {td.progress.total > 0 && (
              <p className="where-pct" style={{ '--tone': `hsl(${Math.round((td.progress.percent / 100) * 130)} 70% 42%)` }}>
                <strong>{td.progress.percent}% completed</strong>
                {td.progress.now && <span className="where-now"> · now on <strong>{td.progress.now.step}</strong> <span className="small muted">(step {td.progress.now.n} of {td.progress.now.of}, job {td.jobs.findIndex((j) => j.id === td.progress.now.job) + 1})</span></span>}
                <span className="small muted"> · {td.progress.completed} of {td.progress.total} jobs completed{td.progress.notDone ? ` · ${td.progress.notDone} not done` : ''}</span>
              </p>
            )}
            {td.jobs.length > 0 && (
              // The day's jobs left to right, in order: completed ones green, the
              // one they're on outlined, the rest as they are.
              <ol className="where-jobs" style={{ '--jobs': td.jobs.length }}>
                {td.jobs.map((j, i) => (
                  <li key={j.id} className={`where-job st-${j.status}${j.id === a.job ? ' now' : ''}`}>
                    <span className="where-job-top">
                      <span className="where-job-n">{i + 1}</span>
                      <span className="chip-slot">{SLOT_LABEL[j.slot] || '–'}</span>
                      <span className={`status status-${j.status}`}>{STATUS_LABEL[j.status]}</span>
                    </span>
                    <strong className="where-job-addr">{j.address || j.postcode}</strong>
                    <span className="small muted">{j.postcode} · {j.reference}</span>
                    <span className="small muted">
                      {j.finishedAt ? `Done ${time(j.finishedAt)}` : j.startedAt ? `On site from ${time(j.startedAt)}` : j.onRouteAt ? `Left ${time(j.onRouteAt)}` : ' '}
                    </span>
                    {j.nextStep && j.stepsDone > 0 && <span className="small where-job-step">Step {j.stepsDone + 1} of 7: {j.nextStep}</span>}
                    {j.reason && ['aborted', 'no_access', 'paused'].includes(j.status) && <span className="small where-job-reason">{j.reason}</span>}
                  </li>
                ))}
              </ol>
            )}
          </section>
        );
      })}

      {data?.unassigned.length > 0 && (
        <section className="card">
          <p className="notice-line notice-warn">{data.unassigned.length} job{data.unassigned.length === 1 ? ' is' : 's are'} booked on this day but not on a team.</p>
        </section>
      )}

      {data?.elsewhere.length > 0 && (
        <section className="card">
          <h2>Also off</h2>
          <ul className="crew where-crew">
            {data.elsewhere.map((p) => <li key={p.id}><User size={14} aria-hidden="true" />{p.name} <Absence a={p.absence} /></li>)}
          </ul>
        </section>
      )}

      {data && !data.teams.length && <section className="card"><p className="muted">No teams with people or jobs on this day.</p></section>}
    </div>
  );
}
